import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Mic, MicOff, Volume2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import ChevronsDown from '@/components/ChevronsDown';
import { toast } from 'sonner';
import { VoiceConfig, defaultConfig } from '@/utils/voiceUtils';
import { useVoiceSupport } from '@/hooks/use-voice-support';
import { useIsMobile } from '@/hooks/use-mobile';
import { processCommand } from '@/utils/voiceCommandUtils';
import { useVoiceCommandExecutor } from '@/hooks/use-voice-commands';
import { Undo2, Redo2 } from 'lucide-react';
import { useCommandHistory } from '@/hooks/use-command-history';
import { playBeep } from '@/utils/audioFeedback';
import { useLocation } from 'react-router-dom';

interface VoiceAssistantProps {
  isActive: boolean;
  onToggle: () => void;
  config?: VoiceConfig;
}

const progressiveMessages = [
  "Working on it...",
  "Executing your command...",
  "One moment please...",
  "Just a second...",
];

const responseVariants = {
  unknown: [
    "Sorry, I didn't understand. Could you rephrase that?",
    "Hmm, I missed that. Try again?",
    "I didn't get that—please repeat.",
  ],
  confirmation: [
    "Are you sure you want to proceed?",
    "This action may be irreversible. Confirm to continue.",
    "Please confirm before I proceed.",
  ],
  done: [
    "All done!",
    "I've completed that for you.",
    "Done as requested.",
  ],
  error: [
    "Something went wrong. Please try again.",
    "Oops—there was a problem!",
    "I encountered an error while executing.",
  ]
};

const VoiceAssistant = ({ isActive, onToggle, config = defaultConfig }: VoiceAssistantProps) => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [response, setResponse] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [commandQueue, setCommandQueue] = useState<string[]>([]);
  const { history, addEntry, undo, redo, clear, canUndo, canRedo } = useCommandHistory();

  const recognitionRef = useRef<any>(null);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { isSupported, hasPermission, permissionError, requestPermission, initializeRecognition } = useVoiceSupport();
  const location = useLocation();

  // Expand/collapse by default for /demo route when button clicked.
  useEffect(() => {
    setIsExpanded(false); // start collapsed
  }, [location.pathname]);

  const setupRecognitionHandlers = useCallback(() => {
    if (!recognitionRef.current) return;

    recognitionRef.current.onresult = (event: any) => {
      const current = event.resultIndex;
      const transcriptText = event.results[current][0].transcript;
      setTranscript(transcriptText);
      handleCommand(transcriptText);
    };

    recognitionRef.current.onerror = (event: any) => {
      setIsListening(false);
      toast.error('Speech recognition error: ' + event.error);
    };

    recognitionRef.current.onend = () => {
      setIsListening(false);
    };
  }, []);

  useEffect(() => {
    if (isSupported && !recognitionRef.current) {
      recognitionRef.current = initializeRecognition();
      setupRecognitionHandlers();
    }
  }, [isSupported, initializeRecognition, setupRecognitionHandlers]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && isListening) {
        recognitionRef.current?.abort();
        setIsListening(false);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isListening]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const speakResponse = useCallback((text: string) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1.0;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;
      try {
        window.speechSynthesis.speak(utterance);
      } catch (error) {
      }
    }
  }, []);

  const executeCommand = useVoiceCommandExecutor({
    config,
    onResponse: (responseText) => {
      setResponse(responseText);
      speakResponse(responseText);
    },
    optionElements: {} // Add accessibility option elements when needed
  });

  useEffect(() => {
    if (isProcessing || commandQueue.length === 0) return;
    const nextCommand = commandQueue[0];
    setIsProcessing(true);
    handleCommand(nextCommand, true);
    setCommandQueue(queue => queue.slice(1));
  }, [commandQueue, isProcessing]);

  const getRandomVariant = (type: keyof typeof responseVariants) => {
    const v = responseVariants[type];
    return v[Math.floor(Math.random() * v.length)];
  };

  const [pendingConfirm, setPendingConfirm] = useState<null | { command: string, onConfirm: () => void }>(null);

  const handleCommand = async (command: string, isFromQueue = false) => {
    if (!command || typeof command !== "string") {
      setResponse(getRandomVariant("unknown"));
      playBeep("error");
      setIsProcessing(false);
      addEntry({ command: command ?? '', response: getRandomVariant("unknown"), isError: true });
      return;
    }

    setIsProcessing(true);
    setResponse(progressiveMessages[Math.floor(Math.random() * progressiveMessages.length)]);
    playBeep("info");
    await new Promise(res => setTimeout(res, 320));

    try {
      const commandResult = processCommand(command, config);

      if (
        typeof command === "string" &&
        /delete|reset|remove|clear/i.test(command) &&
        !isFromQueue
      ) {
        setPendingConfirm({
          command,
          onConfirm: async () => {
            setPendingConfirm(null);
            setResponse("Confirmed. Proceeding...");
            playBeep("info");
            setTimeout(() => handleCommand(command, true), 420);
          }
        });
        setResponse(getRandomVariant("confirmation"));
        playBeep("info");
        setIsProcessing(false);
        return;
      }

      if (commandResult.data && commandResult.data.compound && !isFromQueue) {
        const split = command.split(/\b(?:and|then|&)\b/gi)
          .map(s => s.trim()).filter(Boolean);
        setCommandQueue(split.slice(1));
        await handleCommand(split[0], true);
        setIsProcessing(false);
        return;
      }

      const handled = executeCommand(commandResult);

      if (!handled && commandResult.response) {
        setResponse(commandResult.response);
        speakResponse(commandResult.response);
        playBeep("error");
      }
      else if (handled) {
        setResponse(getRandomVariant("done"));
        speakResponse(getRandomVariant("done"));
        playBeep("success");
      }

      addEntry({
        command,
        response: commandResult.response ?? getRandomVariant("unknown"),
        isError: !commandResult.handled,
      });

    } catch (error) {
      const errorMessage = getRandomVariant("error");
      setResponse(errorMessage);
      speakResponse(errorMessage);
      playBeep("error");
      addEntry({ command, response: errorMessage, isError: true });
    }

    setIsProcessing(false);
  };

  const toggleListening = async () => {
    if (!isSupported) {
      toast.error('Speech recognition is not supported in your browser');
      return;
    }

    if (isListening) {
      recognitionRef.current?.abort();
      setIsListening(false);
    } else {
      if (!hasPermission) {
        const granted = await requestPermission();
        if (!granted) {
          toast.error(permissionError || 'Failed to access microphone');
          return;
        }
      }

      if (!recognitionRef.current) {
        recognitionRef.current = initializeRecognition();
        setupRecognitionHandlers();
      }

      if (!recognitionRef.current) {
        toast.error('Failed to initialize speech recognition');
        return;
      }

      try {
        recognitionRef.current.start();
        setIsListening(true);
        setTranscript('');
        setResponse('');
        if (isMobile && 'vibrate' in navigator) {
          navigator.vibrate(100);
        }
      } catch (error) {
        toast.error('Failed to start speech recognition');
      }
    }
  };

  const handleClearConversation = () => {
    setTranscript('');
    setResponse('');
    clear();
  };

  // --- UI rendering below ---

  // Render the single floating button at bottom center, expands/collapses the panel
  return (
    <>
      {/* Floating button - bottom center (NO overflow, NO duplicates) */}
      <Button
        variant="default"
        size="icon"
        className={cn(
          "fixed left-1/2 -translate-x-1/2 bottom-4 z-50 rounded-full h-16 w-16 flex items-center justify-center shadow-xl hover-scale transition-all bg-white dark:bg-secondary text-primary dark:text-white",
          isExpanded ? "ring-2 ring-primary ring-offset-2" : "",
          !isActive ? "opacity-50 pointer-events-none" : ""
        )}
        aria-label={isExpanded ? "Minimize voice assistant" : "Open voice assistant"}
        onClick={() => setIsExpanded(exp => !exp)}
        style={{
          boxShadow: '0 4px 24px 0 rgba(39,60,144,0.07), 0 1.5px 16px 0 rgba(39,60,144,0.14)',
        }}
      >
        <Mic className="h-8 w-8" aria-hidden="true" />
      </Button>

      {/* Floating panel: only show if expanded */}
      {isExpanded && (
        <div
          className={cn(
            "fixed bottom-24 left-1/2 -translate-x-1/2 w-80 rounded-2xl z-50 shadow-2xl accessibility-glass animate-fade-in bg-background border border-border",
            "max-w-full"
          )}
          role="region"
          aria-label="Voice assistant controls"
          id="voice-assistant-panel"
        >
          <div className="p-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-base font-medium" id="voice-assistant-title">
                Voice Assistant {!isSupported && "(Not Supported)"}
              </h3>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleClearConversation}
                  aria-label="Clear conversation"
                  className="h-6 w-6 text-foreground"
                >
                  <X className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={undo}
                  aria-label="Undo last command"
                  className="h-6 w-6 text-foreground"
                  disabled={!canUndo}
                >
                  <Undo2 className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={redo}
                  aria-label="Redo last command"
                  className="h-6 w-6 text-foreground"
                  disabled={!canRedo}
                >
                  <Redo2 className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsExpanded(false)}
                  aria-label="Minimize voice assistant"
                  className="h-6 w-6 text-foreground"
                >
                  <ChevronsDown className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div
              className="bg-background/60 rounded-lg p-3 mb-3 h-32 overflow-y-auto border border-border"
              role="log"
              aria-live="polite"
              aria-label="Voice assistant conversation"
            >
              {!isSupported ? (
                <p className="text-sm text-destructive">
                  Speech recognition is not supported in your browser. Please try using Chrome, Edge, or Safari.
                </p>
              ) : permissionError ? (
                <p className="text-sm text-destructive">
                  {permissionError}
                </p>
              ) : (
                <>
                  {history.length > 0 ? (
                    <ul className="space-y-1">
                      {history.map((entry, i) => (
                        <li key={i}>
                          <div>
                            <span className="text-xs text-muted-foreground">You said:</span>
                            <span className={`ml-2 text-sm ${entry.isError ? 'text-destructive' : ''}`}>{entry.command}</span>
                          </div>
                          <div>
                            <span className="text-xs text-muted-foreground">Assistant:</span>
                            <span className={`ml-2 text-sm ${entry.isError ? 'text-destructive' : ''}`}>{entry.response}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="mb-2">
                      <p className="text-xs text-muted-foreground">You said:</p>
                      <p className="text-sm">{transcript}</p>
                    </div>
                  )}
                </>
              )}

              {isProcessing && (
                <div className="flex gap-1 items-center text-sm text-muted-foreground">
                  <span className="animate-pulse">●</span>
                  <span className="animate-pulse delay-100">●</span>
                  <span className="animate-pulse delay-200">●</span>
                </div>
              )}

              {response && (
                <div>
                  <p className="text-xs text-muted-foreground">Assistant:</p>
                  <p className="text-sm">{response}</p>
                </div>
              )}

              {!transcript && !response && !isProcessing && isSupported && (
                <p className="text-sm text-muted-foreground text-center mt-10">
                  Press the microphone below to speak
                </p>
              )}
            </div>
            {/* Mic listening button inside the panel */}
            <Button
              variant={isListening ? "destructive" : "default"}
              className={cn(
                "w-full flex items-center justify-center gap-2 text-primary-foreground",
                isListening && "animate-pulse"
              )}
              onClick={toggleListening}
              disabled={!isSupported || !isActive}
              aria-label={isListening ? "Stop listening" : "Start listening"}
              aria-pressed={isListening}
            >
              {isListening ? (
                <>
                  <MicOff className="h-4 w-4" /> Stop Listening
                </>
              ) : (
                <>
                  <Mic className="h-4 w-4" /> Start Listening
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Confirmation dialog overlay (inside expanded panel if shown) */}
      {isExpanded && pendingConfirm && (
        <div className="fixed inset-0 z-60 bg-black/30 flex justify-center items-center">
          <div className="p-4 bg-background border rounded-lg shadow-lg w-80 animate-fade-in">
            <div className="mb-2 text-primary font-semibold">Confirmation Required</div>
            <div className="mb-4 text-sm">{getRandomVariant("confirmation")}</div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="default"
                onClick={() => {
                  pendingConfirm.onConfirm();
                }}
              >Confirm</Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setPendingConfirm(null);
                  setResponse("Cancelled.");
                  playBeep("info");
                }}
              >Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VoiceAssistant;
